// TwinBomViewer - Combined 3D viewer + BOM with bidirectional selection
// Redesigned with vertical layout and link mode
import { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from '../hooks/useTranslation'
import { CADViewer } from './CADViewer'
import {
  CubeTransparentIcon,
  TableCellsIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  LinkIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline'
import { CubeIcon as CubeIconSolid } from '@heroicons/react/24/solid'
import type { AssetBomItemRecord } from '../../shared/types'
import { assetsApi } from '../lib/api'

// Extend BOM item with optional twin part linkage
export type BomItem = AssetBomItemRecord & {
  twinPartId?: string | null
}

interface TwinBomViewerProps {
  assetId: string
  projectId?: string | null
  sampleId?: string | null
  bomItems: BomItem[]
  className?: string
  onBomItemUpdate?: (updatedItem: BomItem) => void
}

type ViewLayout = 'split' | 'viewer' | 'bom'

interface FloatingDetailPanelProps {
  item: BomItem | null
  isLinked: boolean
  onClose: () => void
  onRequestPart?: (item: BomItem) => void
}

// Floating detail panel component
function FloatingDetailPanel({ item, isLinked, onClose, onRequestPart }: FloatingDetailPanelProps) {
  const { t } = useTranslation()

  if (!item) return null

  return (
    <div className="absolute top-4 left-4 z-20 bg-white rounded-lg shadow-lg border border-gray-200 p-4 min-w-[280px] max-w-[350px]">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {isLinked ? (
            <CubeIconSolid className="h-5 w-5 text-brand-500" />
          ) : (
            <CubeTransparentIcon className="h-5 w-5 text-gray-400" />
          )}
          <span className="font-semibold text-gray-900">{item.partNumber}</span>
        </div>
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600 rounded"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>

      {item.description && (
        <p className="text-sm text-gray-600 mb-3">{item.description}</p>
      )}

      <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
        <span>
          {t('twin.bomViewer.quantity')}: <strong className="text-gray-700">{item.quantity}</strong>
        </span>
        {item.listPrice && (
          <span>
            {item.currency} <strong className="text-gray-700">{item.listPrice}</strong>
          </span>
        )}
      </div>

      {item.supersededBy && (
        <div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded mb-3">
          {t('twin.bomViewer.supersededBy')}: {item.supersededBy}
        </div>
      )}

      {onRequestPart && (
        <button
          onClick={() => onRequestPart(item)}
          className="flex items-center gap-2 w-full justify-center px-3 py-2 bg-brand-50 text-brand-700 rounded-md hover:bg-brand-100 text-sm font-medium"
        >
          <ShoppingCartIcon className="h-4 w-4" />
          {t('twin.bomViewer.requestPart')}
        </button>
      )}
    </div>
  )
}

// Link mode instruction overlay
function LinkModeOverlay({ bomItem, onCancel }: { bomItem: BomItem; onCancel: () => void }) {
  const { t } = useTranslation()

  return (
    <div className="absolute inset-0 z-10 pointer-events-none">
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black/70 text-white px-6 py-4 rounded-lg text-center pointer-events-auto">
        <p className="text-sm mb-2">{t('twin.bomViewer.linkModeInstruction')}</p>
        <p className="font-semibold text-brand-300">&ldquo;{bomItem.partNumber}&rdquo;</p>
        <button
          onClick={onCancel}
          className="mt-3 px-3 py-1 text-xs bg-white/20 hover:bg-white/30 rounded"
        >
          {t('twin.bomViewer.cancelLinking')} (ESC)
        </button>
      </div>
    </div>
  )
}

export function TwinBomViewer({
  assetId,
  projectId,
  sampleId,
  bomItems: initialBomItems,
  className = '',
  onBomItemUpdate,
}: TwinBomViewerProps) {
  const { t } = useTranslation()
  const [bomItems, setBomItems] = useState<BomItem[]>(initialBomItems)
  const [selectedPartId, setSelectedPartId] = useState<string | null>(null)
  const [hoveredPartId, setHoveredPartId] = useState<string | null>(null)
  const [layout, setLayout] = useState<ViewLayout>('split')
  const [bomPanelCollapsed, setBomPanelCollapsed] = useState(false)
  const [linkMode, setLinkMode] = useState<{ active: boolean; bomItem: BomItem | null }>({ active: false, bomItem: null })
  const [selectedBomItem, setSelectedBomItem] = useState<BomItem | null>(null)
  const [isLinking, setIsLinking] = useState(false)
  const bomTableRef = useRef<HTMLDivElement>(null)

  // Update bomItems when props change
  useEffect(() => {
    setBomItems(initialBomItems)
  }, [initialBomItems])

  // Handle ESC key to cancel link mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && linkMode.active) {
        setLinkMode({ active: false, bomItem: null })
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [linkMode.active])

  // Find BOM item by part ID (either twinPartId or partNumber match)
  const findBomItemByPartId = useCallback((partId: string): BomItem | undefined => {
    return bomItems.find(item =>
      item.twinPartId === partId ||
      item.partNumber.toLowerCase() === partId.toLowerCase()
    )
  }, [bomItems])

  // Handle part click from 3D viewer
  const handleViewerPartClick = useCallback(async (partId: string, partName: string) => {
    // If in link mode, link the clicked part to the selected BOM item
    if (linkMode.active && linkMode.bomItem) {
      setIsLinking(true)
      try {
        const updated = await assetsApi.updateBomItem(assetId, linkMode.bomItem.id, {
          twinPartId: partId,
        })
        // Update local state
        setBomItems(prev => prev.map(item =>
          item.id === linkMode.bomItem!.id ? { ...item, twinPartId: updated.twinPartId } : item
        ))
        onBomItemUpdate?.({ ...linkMode.bomItem, twinPartId: updated.twinPartId })
        setLinkMode({ active: false, bomItem: null })
      } catch (error) {
        console.error('Failed to link part:', error)
      } finally {
        setIsLinking(false)
      }
      return
    }

    // Normal click - select the part
    setSelectedPartId(partId)

    // Find and select corresponding BOM item
    const bomItem = findBomItemByPartId(partId)
    if (bomItem) {
      setSelectedBomItem(bomItem)
      // Scroll to BOM row
      const row = document.getElementById(`bom-row-${bomItem.id}`)
      row?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    } else {
      setSelectedBomItem(null)
    }
  }, [assetId, linkMode, findBomItemByPartId, onBomItemUpdate])

  // Handle BOM row click - select part in viewer
  const handleBomRowClick = useCallback((item: BomItem) => {
    const partId = item.twinPartId || item.partNumber
    setSelectedPartId(partId)
    setSelectedBomItem(item)
  }, [])

  // Handle BOM row double-click - zoom to part (CADViewer handles zoom via selectedPart)
  const handleBomRowDoubleClick = useCallback((item: BomItem) => {
    const partId = item.twinPartId || item.partNumber
    setSelectedPartId(partId)
    setSelectedBomItem(item)
  }, [])

  // Handle BOM row hover
  const handleBomRowHover = useCallback((item: BomItem | null) => {
    if (item) {
      const partId = item.twinPartId || item.partNumber
      setHoveredPartId(partId)
    } else {
      setHoveredPartId(null)
    }
  }, [])

  // Check if a BOM item is selected
  const isItemSelected = useCallback((item: BomItem) => {
    if (!selectedPartId) return false
    return item.twinPartId === selectedPartId ||
      item.partNumber.toLowerCase() === selectedPartId.toLowerCase()
  }, [selectedPartId])

  // Check if a BOM item is hovered
  const isItemHovered = useCallback((item: BomItem) => {
    if (!hoveredPartId) return false
    return item.twinPartId === hoveredPartId ||
      item.partNumber.toLowerCase() === hoveredPartId.toLowerCase()
  }, [hoveredPartId])

  // Enter link mode for a BOM item
  const enterLinkMode = useCallback((item: BomItem) => {
    setLinkMode({ active: true, bomItem: item })
    setSelectedBomItem(item)
  }, [])

  // Unlink a BOM item from its 3D part
  const unlinkPart = useCallback(async (item: BomItem) => {
    setIsLinking(true)
    try {
      const updated = await assetsApi.updateBomItem(assetId, item.id, { twinPartId: null })
      setBomItems(prev => prev.map(i =>
        i.id === item.id ? { ...i, twinPartId: null } : i
      ))
      onBomItemUpdate?.({ ...item, twinPartId: null })
    } catch (error) {
      console.error('Failed to unlink part:', error)
    } finally {
      setIsLinking(false)
    }
  }, [assetId, onBomItemUpdate])

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedPartId(null)
    setSelectedBomItem(null)
    setHoveredPartId(null)
  }, [])

  // Check if item has a link
  const isItemLinked = (item: BomItem) => Boolean(item.twinPartId)

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Layout toggle toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-b border-gray-200 rounded-t-lg">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">{t('twin.bomViewer.view')}:</span>
          <div className="flex rounded-md shadow-sm">
            <button
              onClick={() => setLayout('split')}
              className={`px-3 py-1.5 text-sm font-medium rounded-l-md border ${
                layout === 'split'
                  ? 'bg-brand-600 text-white border-brand-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {t('twin.bomViewer.split')}
            </button>
            <button
              onClick={() => setLayout('viewer')}
              className={`px-3 py-1.5 text-sm font-medium border-t border-b ${
                layout === 'viewer'
                  ? 'bg-brand-600 text-white border-brand-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <CubeTransparentIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setLayout('bom')}
              className={`px-3 py-1.5 text-sm font-medium rounded-r-md border ${
                layout === 'bom'
                  ? 'bg-brand-600 text-white border-brand-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <TableCellsIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Link Parts button */}
          {layout !== 'bom' && bomItems.length > 0 && (
            <button
              onClick={() => {
                if (linkMode.active) {
                  setLinkMode({ active: false, bomItem: null })
                } else if (selectedBomItem) {
                  enterLinkMode(selectedBomItem)
                }
              }}
              disabled={!linkMode.active && !selectedBomItem}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                linkMode.active
                  ? 'bg-amber-100 text-amber-700 border-amber-300'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
              }`}
              title={t('twin.bomViewer.linkPartsTooltip')}
            >
              <LinkIcon className="h-4 w-4" />
              {linkMode.active ? t('twin.bomViewer.linking') : t('twin.bomViewer.linkParts')}
            </button>
          )}

          {selectedPartId && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{t('twin.bomViewer.selected')}:</span>
              <span className="text-xs font-medium text-brand-600 max-w-[150px] truncate">{selectedPartId}</span>
              <button
                onClick={clearSelection}
                className="text-xs text-gray-400 hover:text-gray-600"
              >
                {t('twin.bomViewer.clear')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content area - Vertical split */}
      <div className="flex-1 flex flex-col min-h-0 overflow-auto">
        {/* 3D Viewer (Top section) - Square based on width */}
        {(layout === 'split' || layout === 'viewer') && (
          <div
            className={`relative ${
              layout === 'viewer' ? 'flex-1' : 'w-full aspect-square max-h-[60vh]'
            } border-b border-gray-200`}
          >
            <CADViewer
              projectId={projectId ?? undefined}
              sampleId={sampleId ?? undefined}
              style={{ width: '100%', height: '100%' }}
              onPartSelect={(partName) => {
                if (partName) {
                  handleViewerPartClick(partName, partName)
                }
              }}
              selectedPart={selectedPartId}
              highlightedParts={hoveredPartId ? [hoveredPartId] : []}
            />

            {/* Link mode overlay */}
            {linkMode.active && linkMode.bomItem && (
              <LinkModeOverlay
                bomItem={linkMode.bomItem}
                onCancel={() => setLinkMode({ active: false, bomItem: null })}
              />
            )}

            {/* Floating detail panel */}
            {selectedBomItem && !linkMode.active && (
              <FloatingDetailPanel
                item={selectedBomItem}
                isLinked={isItemLinked(selectedBomItem)}
                onClose={() => setSelectedBomItem(null)}
              />
            )}
          </div>
        )}

        {/* BOM List (Bottom section) */}
        {(layout === 'split' || layout === 'bom') && (
          <div
            className={`flex flex-col ${
              layout === 'split'
                ? bomPanelCollapsed ? 'h-10 flex-none' : 'flex-1'
                : 'flex-1'
            } bg-white transition-all duration-200`}
          >
            {/* Collapsible header (only in split mode) */}
            {layout === 'split' && (
              <div
                className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100"
                onClick={() => setBomPanelCollapsed(!bomPanelCollapsed)}
              >
                <div className="flex items-center gap-2">
                  <TableCellsIcon className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">
                    {t('twin.bomViewer.bomTitle')} ({bomItems.length})
                  </span>
                </div>
                {bomPanelCollapsed ? (
                  <ChevronUpIcon className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                )}
              </div>
            )}

            {/* BOM Table */}
            {(!bomPanelCollapsed || layout === 'bom') && (
              <div ref={bomTableRef} className="flex-1 overflow-auto">
                {bomItems.length > 0 ? (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="sticky left-0 z-20 bg-gray-50 px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-12">
                          {/* Link status */}
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-16">
                          {t('twin.bomViewer.position')}
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[150px]">
                          {t('twin.bomViewer.partNumber')}
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[200px]">
                          {t('twin.bomViewer.description')}
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">
                          {t('twin.bomViewer.quantity')}
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">
                          {t('twin.bomViewer.price')}
                        </th>
                        <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">
                          {t('twin.bomViewer.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {bomItems.map((item) => {
                        const linked = isItemLinked(item)
                        const selected = isItemSelected(item)
                        const hovered = isItemHovered(item)
                        const inLinkMode = linkMode.active && linkMode.bomItem?.id === item.id

                        return (
                          <tr
                            key={item.id}
                            id={`bom-row-${item.id}`}
                            onClick={() => handleBomRowClick(item)}
                            onDoubleClick={() => handleBomRowDoubleClick(item)}
                            onMouseEnter={() => handleBomRowHover(item)}
                            onMouseLeave={() => handleBomRowHover(null)}
                            className={`cursor-pointer transition-colors ${
                              inLinkMode
                                ? 'bg-amber-100 border-l-4 border-l-amber-500'
                                : selected
                                ? 'bg-brand-100 border-l-4 border-l-brand-500'
                                : hovered
                                ? 'bg-brand-50'
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            {/* Link status indicator */}
                            <td className="sticky left-0 bg-inherit whitespace-nowrap px-3 py-3">
                              {linked ? (
                                <CubeIconSolid
                                  className="h-5 w-5 text-brand-500"
                                  title={`${t('twin.bomViewer.linkedTo3D')}: ${item.twinPartId}`}
                                />
                              ) : (
                                <CubeTransparentIcon
                                  className="h-5 w-5 text-gray-300"
                                  title={t('twin.bomViewer.notLinked')}
                                />
                              )}
                            </td>
                            <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                              {item.position}
                            </td>
                            <td className="whitespace-nowrap px-3 py-3 text-sm font-medium text-gray-900">
                              {item.partNumber}
                            </td>
                            <td className="px-3 py-3 text-sm text-gray-500 max-w-xs truncate">
                              {item.description || '-'}
                            </td>
                            <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                              {item.quantity}
                            </td>
                            <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                              {item.listPrice
                                ? `${item.currency || ''} ${item.listPrice}`
                                : '-'
                              }
                            </td>
                            {/* Row actions */}
                            <td className="whitespace-nowrap px-3 py-3 text-sm text-right">
                              <div className="flex items-center justify-end gap-1">
                                {linked ? (
                                  <>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleBomRowClick(item)
                                      }}
                                      className="p-1 text-gray-400 hover:text-brand-600 rounded"
                                      title={t('twin.bomViewer.zoomToPart')}
                                    >
                                      <MagnifyingGlassIcon className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        unlinkPart(item)
                                      }}
                                      disabled={isLinking}
                                      className="p-1 text-gray-400 hover:text-red-600 rounded disabled:opacity-50"
                                      title={t('twin.bomViewer.unlinkPart')}
                                    >
                                      <XMarkIcon className="h-4 w-4" />
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      enterLinkMode(item)
                                    }}
                                    disabled={linkMode.active}
                                    className="p-1 text-gray-400 hover:text-brand-600 rounded disabled:opacity-50"
                                    title={t('twin.bomViewer.linkToPart')}
                                  >
                                    <LinkIcon className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500 py-12">
                    <TableCellsIcon className="h-12 w-12 text-gray-300 mb-3" />
                    <p className="text-sm">{t('twin.bomViewer.noBomItems')}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {t('twin.bomViewer.uploadBomPrompt')}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Instructions footer */}
      {layout === 'split' && bomItems.length > 0 && !bomPanelCollapsed && !linkMode.active && (
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 rounded-b-lg">
          <p className="text-xs text-gray-500 text-center">
            {t('twin.bomViewer.tip')}
          </p>
        </div>
      )}
    </div>
  )
}
